// "Print or save" (boards PrintChoiceDesk, PrintChoicePhone; docs/plans/bill-logo.md): one
// dialog for a bill's paper (A4 or the 80 mm receipt) and what to do with it: print it,
// download it as a PDF, or hand the PDF to the phone's share sheet. A PDF is a copy of the
// bill like a print, so it is counted and labelled the same way.
import { Button, Modal, cx, useToast } from '@ims/ui'
import { CheckCircle2, Copy, Download, Printer, Share2 } from '@ims/ui/icons'
import { useEffect, useMemo, useState } from 'react'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { downloadBlob } from '../lib/download.ts'
import { money } from '../lib/format.ts'
import { finePointer } from '../lib/pointer.ts'
import { readPrintChoice, savePrintChoice } from '../lib/print-choice.ts'
import { canShareFile, shareFile } from '../lib/share.ts'
import { printedLine } from '../lib/bill-view.ts'
import type { A4Doc } from '../lib/doc-view.ts'
import { A4Page, printDocument, printName, viewOf, type PrintFormat } from '../print.tsx'
import { useRepo } from '../workspace.tsx'
import {
  billDocument,
  loadBillPrint,
  nextCopy,
  printBill,
  recordPrint,
  useBillMeta,
  usePrintedBy,
  type BillPrintData,
} from './order-actions.tsx'

/** Whether this browser can hand a PDF to the share sheet (asked once). */
export const sharesFiles =
  typeof File !== 'undefined' &&
  canShareFile(new File([''], 'bill.pdf', { type: 'application/pdf' }))

export const PAPERS: { format: PrintFormat; title: string; about: string }[] = [
  { format: 'a4', title: 'A4 bill', about: 'Full page, for files and customers who keep bills' },
  { format: 'receipt', title: 'Receipt, 80 mm', about: "For the counter's thermal printer" },
]

/** Which copy the next print or PDF will be (`nextCopy`). */
type PrintCopy = { copyNo: number; label: string | null }

/** How a Download or Share ended: done, the share sheet closed, or an error (toasted). */
export type SaveResult = 'done' | 'closed' | 'failed'

/**
 * A bill's print and PDF, for "Print or save" and the print menu on a list row (print-menu.tsx):
 * the bill, which copy the next one will be, Print, Download and Share. A print or a PDF is a
 * copy like any other, so both are counted. `sharePaper` names the paper a Share would use:
 * its file is made ahead, because the share sheet must open straight from the tap.
 */
export function useBillOutput(
  invoiceId: string,
  serverName: (id: string | null) => string | null,
  sharePaper: PrintFormat | null,
) {
  const repo = useRepo()
  const settings = useSettings()
  const toast = useToast()
  const billMeta = useBillMeta()
  const data = useLive(
    (r) => loadBillPrint(r, invoiceId, serverName),
    [invoiceId],
    ['sales_invoices', 'payments', 'payment_allocations', 'credit_notes'],
  ).data
  const invoice = data?.detail.invoice
  // Which copy the next print or PDF will be: counted across devices (invoice_prints).
  const copy = useLive(
    async (r) => {
      const inv = await r.get('sales_invoices', invoiceId)
      return inv ? nextCopy(r, inv) : null
    },
    [invoiceId],
    ['invoice_prints', 'sales_invoices'],
  ).data

  const [ready, setReady] = useState<{ key: string; file: File } | null>(null)
  const key = `${sharePaper}|${copy?.copyNo ?? ''}|${invoice?.updated_at ?? ''}`
  useEffect(() => {
    if (!sharesFiles || !sharePaper || !data || !copy) return
    let live = true
    void makeFile(data, sharePaper, copy.label).then(
      (file) => live && setReady({ key, file }),
      () => {},
    )
    return () => {
      live = false
    }
    // `key` names everything the file is made from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  async function makeFile(
    d: BillPrintData,
    format: PrintFormat,
    label: string | null,
  ): Promise<File> {
    const meta = await billMeta(repo, d.detail.invoice)
    const view = viewOf({
      invoice: d.detail.invoice,
      lines: d.detail.lines,
      settings,
      balancePaisa: d.detail.balancePaisa,
      batches: d.batches,
      adjustments: d.adjustments,
      copyLabel: label,
      service: d.service,
      meta,
    })
    const { makeBillPdf } = await import('../bill-pdf.ts')
    const pdf = await makeBillPdf(view, format)
    const name = printName(format === 'a4' ? 'Invoice' : 'Receipt', d.detail.invoice)
    return new File([pdf.blob], `${name}.pdf`, { type: 'application/pdf' })
  }

  // A menu row may be picked before the live reads above have answered: read it now then.
  const current = async (): Promise<{ data: BillPrintData; copy: PrintCopy } | null> => {
    if (data && copy) return { data, copy }
    const d = await loadBillPrint(repo, invoiceId, serverName)
    return d ? { data: d, copy: await nextCopy(repo, d.detail.invoice) } : null
  }

  const print = async (format: PrintFormat): Promise<void> => {
    const now = await current()
    if (!now) return
    const inv = now.data.detail.invoice
    const meta = await billMeta(repo, inv)
    await printBill(
      repo,
      inv.tenant_id,
      inv,
      format,
      (label) => billDocument(format, now.data, settings, label, meta),
      (error) =>
        toast.error('Printed, but the print was not recorded', { detail: errorMessage(error) }),
    )
  }

  const save = async (how: 'pdf' | 'share', format: PrintFormat): Promise<SaveResult> => {
    try {
      const now = await current()
      if (!now) return 'failed'
      const file =
        how === 'share' && format === sharePaper && ready?.key === key
          ? ready.file
          : await makeFile(now.data, format, now.copy.label)
      if (how === 'share') {
        const result = await shareFile(file)
        if (result === 'closed') return 'closed'
        if (result === 'downloaded') toast.success('PDF downloaded')
      } else {
        downloadBlob(file, file.name)
        toast.success('PDF downloaded')
      }
      await recordPrint(repo, now.data.detail.invoice, format, now.copy.copyNo).catch(
        (error: unknown) =>
          toast.error('Saved, but the copy was not recorded', { detail: errorMessage(error) }),
      )
      return 'done'
    } catch (error) {
      toast.error(errorMessage(error))
      return 'failed'
    }
  }

  return { data, invoice, copy, print, save }
}

export function PrintDialog({
  invoiceId,
  serverName,
  onClose,
}: {
  invoiceId: string
  serverName: (id: string | null) => string | null
  onClose: () => void
}) {
  const settings = useSettings()
  const [paper, setPaper] = useState<PrintFormat>(readPrintChoice)
  const [busy, setBusy] = useState<'print' | 'pdf' | 'share' | null>(null)
  const {
    data,
    invoice,
    copy,
    print: printOn,
    save: saveOn,
  } = useBillOutput(invoiceId, serverName, paper)

  const choose = (format: PrintFormat) => {
    setPaper(format)
    savePrintChoice(format)
  }

  const print = async () => {
    setBusy('print')
    try {
      await printOn(paper)
      onClose()
    } finally {
      setBusy(null)
    }
  }

  const save = async (how: 'pdf' | 'share') => {
    setBusy(how)
    try {
      if ((await saveOn(how, paper)) === 'done') onClose()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      unsaved={false}
      title="Print or save"
      size="md"
      footer={
        <PrintActions
          loading={!data || !copy}
          busy={busy}
          onPrint={() => void print()}
          onSave={(how) => void save(how)}
        />
      }
    >
      <div className="space-y-4">
        {invoice && (
          <p className="-mt-1 text-sm text-muted">
            {[
              invoice.number ?? 'Number on sync',
              invoice.customer_name,
              money(invoice.total_paisa, settings),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        <div>
          <p id="print-paper" className="mb-2 text-sm font-semibold text-muted">
            Paper
          </p>
          <div role="group" aria-labelledby="print-paper" className="grid grid-cols-2 gap-3">
            {PAPERS.map((p) => {
              const on = paper === p.format
              return (
                <button
                  key={p.format}
                  type="button"
                  aria-pressed={on}
                  onClick={() => choose(p.format)}
                  className={cx(
                    'relative rounded-card p-2.5 text-left transition-colors',
                    on
                      ? 'border-2 border-primary bg-primary-light'
                      : 'border border-border bg-surface hover:bg-soft',
                  )}
                >
                  {on && (
                    <CheckCircle2
                      aria-hidden
                      className="absolute top-2 right-2 size-5 text-primary"
                    />
                  )}
                  <PaperPicture
                    format={p.format}
                    data={data ?? null}
                    settings={settings}
                    label={copy?.label ?? null}
                  />
                  <span className="mt-2 block font-bold text-ink">{p.title}</span>
                  <span className="block text-xs text-muted">{p.about}</span>
                </button>
              )
            })}
          </div>
        </div>
        {copy && invoice?.status !== 'draft' && (
          <p
            className={cx(
              'flex items-start gap-2 rounded-xl px-3 py-2 text-sm',
              copy.label ? 'bg-warning-light text-warning' : 'text-muted',
            )}
          >
            {copy.label && <Copy aria-hidden className="mt-0.5 size-4 shrink-0" />}
            {copy.label
              ? `Printed before. This one will say ${copy.label}, printed or saved as PDF.`
              : 'First print of this bill.'}
          </p>
        )}
      </div>
    </Modal>
  )
}

/** The bill on the chosen paper, drawn small: what will come out. */
function PaperPicture({
  format,
  data,
  settings,
  label,
}: {
  format: PrintFormat
  data: BillPrintData | null
  settings: ReturnType<typeof useSettings>
  label: string | null
}) {
  const width = format === 'a4' ? 794 : 280
  const scale = format === 'a4' ? 0.15 : 0.4
  return (
    <span className="flex h-28 justify-center overflow-hidden rounded-lg bg-neutral-wash pt-2">
      <span
        aria-hidden
        className="block overflow-hidden bg-white shadow-card"
        style={{ width: width * scale, height: 104 }}
      >
        {data && (
          <span
            className="block"
            style={{
              width,
              transform: `scale(${scale})`,
              transformOrigin: '0 0',
              padding: format === 'a4' ? 48 : 8,
            }}
          >
            {billDocument(format, data, settings, label, {})}
          </span>
        )}
      </span>
    </span>
  )
}

/**
 * The buttons under Print or save: Print, Download PDF and, where the browser can hand a file
 * to the share sheet, Share PDF. On a touch screen with sharing, Share leads.
 */
function PrintActions({
  loading,
  busy,
  onPrint,
  onSave,
}: {
  loading: boolean
  busy: 'print' | 'pdf' | 'share' | null
  onPrint: () => void
  onSave: (how: 'pdf' | 'share') => void
}) {
  const shareFirst = sharesFiles && !finePointer
  const shareButton = (main: boolean) => (
    <Button
      variant={main ? 'primary' : 'secondary'}
      size={main ? 'lg' : 'md'}
      className={cx(main && 'w-full')}
      icon={<Share2 className="size-4" />}
      disabled={loading}
      loading={busy === 'share'}
      onClick={() => onSave('share')}
    >
      Share PDF
    </Button>
  )
  const downloadButton = (
    <Button
      variant="secondary"
      className={cx(!finePointer && 'w-full')}
      icon={<Download className="size-4" />}
      disabled={loading}
      loading={busy === 'pdf'}
      onClick={() => onSave('pdf')}
    >
      Download PDF
    </Button>
  )
  const printButton = (main: boolean) => (
    <Button
      variant={main ? 'primary' : 'secondary'}
      className={cx(!finePointer && 'w-full')}
      icon={<Printer className="size-4" />}
      disabled={loading}
      loading={busy === 'print'}
      onClick={onPrint}
    >
      Print
    </Button>
  )
  return finePointer ? (
    <div className="flex w-full flex-wrap items-center gap-2">
      {sharesFiles && shareButton(false)}
      <span className="ml-auto flex flex-wrap gap-2">
        {downloadButton}
        {printButton(true)}
      </span>
    </div>
  ) : (
    <div className="grid w-full gap-2">
      {shareFirst ? shareButton(true) : null}
      <div className="grid grid-cols-2 gap-2">
        {printButton(!shareFirst)}
        {downloadButton}
      </div>
    </div>
  )
}

/** A document ready for Print or save. */
export interface DocToPrint {
  doc: A4Doc
  /** The file and print name (`printName`): "Quotation-QT-…". */
  name: string
  /** The line under the dialog's title: number, who for, total. */
  summary: string
}

/**
 * Print or save for a quotation, a sales order or a purchase order (board DocPrintDialog):
 * A4 only, so a picture of page 1 instead of a paper choice. Paperwork is not a bill, so
 * nothing is counted or recorded. `doc` comes without "Printed by": it is stamped at the tap.
 */
/**
 * A document's print and PDF (a quotation, an order or a purchase order), for its Print or
 * save and the print menu on a list row: A4 only, nothing counted. "Printed by" is stamped
 * at the tap; the file for Share is made ahead, so the share sheet opens straight from it.
 */
export function useDocOutput(item: DocToPrint | null) {
  const toast = useToast()
  const printedBy = usePrintedBy()
  const stamp = (doc: A4Doc): A4Doc => {
    const p = printedBy()
    return { ...doc, printed: printedLine(p.printedBy, p.printedAt) }
  }
  const makeFile = async (it: DocToPrint): Promise<File> => {
    const { makeA4Pdf } = await import('../a4-pdf.ts')
    const pdf = await makeA4Pdf(stamp(it.doc))
    return new File([pdf.blob], `${it.name}.pdf`, { type: 'application/pdf' })
  }
  const [ready, setReady] = useState<{ doc: A4Doc; file: File } | null>(null)
  useEffect(() => {
    if (!sharesFiles || !item) return
    let live = true
    void makeFile(item).then(
      (file) => live && setReady({ doc: item.doc, file }),
      () => {},
    )
    return () => {
      live = false
    }
    // `item.doc` is everything the file is made from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.doc])

  // `it`: the document when it was loaded after this render (a menu row picked at once).
  const print = (it: DocToPrint | null = item) => {
    if (it) printDocument(<A4Page doc={stamp(it.doc)} />, 'a4', it.name)
  }
  const save = async (how: 'pdf' | 'share', it: DocToPrint | null = item): Promise<SaveResult> => {
    if (!it) return 'failed'
    try {
      const file = how === 'share' && ready?.doc === it.doc ? ready.file : await makeFile(it)
      if (how === 'share') {
        const result = await shareFile(file)
        if (result === 'closed') return 'closed'
        if (result === 'downloaded') toast.success('PDF downloaded')
      } else {
        downloadBlob(file, file.name)
        toast.success('PDF downloaded')
      }
      return 'done'
    } catch (error) {
      toast.error(errorMessage(error))
      return 'failed'
    }
  }
  return { print, save }
}

export function DocPrintDialog({
  doc,
  name,
  summary,
  onClose,
}: DocToPrint & { onClose: () => void }) {
  const [busy, setBusy] = useState<'print' | 'pdf' | 'share' | null>(null)
  const out = useDocOutput(useMemo(() => ({ doc, name, summary }), [doc, name, summary]))

  const print = () => {
    setBusy('print')
    out.print()
    setBusy(null)
    onClose()
  }
  const save = async (how: 'pdf' | 'share') => {
    setBusy(how)
    try {
      if ((await out.save(how)) === 'done') onClose()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      unsaved={false}
      title="Print or save"
      size="md"
      footer={
        <PrintActions
          loading={false}
          busy={busy}
          onPrint={print}
          onSave={(how) => void save(how)}
        />
      }
    >
      <div className="space-y-3">
        <p className="-mt-1 text-sm text-muted">{summary}</p>
        <div className="flex justify-center rounded-xl bg-neutral-wash p-2.5">
          <span
            aria-hidden
            className="block overflow-hidden bg-white shadow-card"
            style={{ width: 794 * 0.2, height: 1123 * 0.2 }}
          >
            <span
              className="block"
              style={{ width: 794, transform: 'scale(0.2)', transformOrigin: '0 0', padding: 48 }}
            >
              <A4Page doc={doc} />
            </span>
          </span>
        </div>
        <p className="text-sm text-muted">
          {doc.seller.logo ? 'A4, with your logo.' : 'A4.'} Share PDF sends the file itself, so they
          can keep it.
        </p>
      </div>
    </Modal>
  )
}
